import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { toast } from '../components/Toast/ToastContainer';
import '../styles/TwitterTools.css';

const YouTubeCommentPicker = () => {
    const { isAuthenticated, user } = useContext(AuthContext);
    const [videoUrl, setVideoUrl] = useState('');
    const [numberOfWinners, setNumberOfWinners] = useState(1);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [videoInfo, setVideoInfo] = useState(null);

    // Filter states
    const [uniqueAuthors, setUniqueAuthors] = useState(false);
    const [includeReplies, setIncludeReplies] = useState(false);
    const [textSearch, setTextSearch] = useState('');
    const [beforeDate, setBeforeDate] = useState('');
    const [afterDate, setAfterDate] = useState('');

    // Verification states
    const [verificationId, setVerificationId] = useState('');
    const [verificationResult, setVerificationResult] = useState(null);
    const [verifyingRoll, setVerifyingRoll] = useState(false);
    const [verificationError, setVerificationError] = useState('');

    // Reroll states
    const [showRerollModal, setShowRerollModal] = useState(false);
    const [rerollReason, setRerollReason] = useState('');
    const [rerolling, setRerolling] = useState(false);

    // History states
    const [giveawayHistory, setGiveawayHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [recentGiveaways, setRecentGiveaways] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(false);
    const [showRecentGiveaways, setShowRecentGiveaways] = useState(false);

    const handlePickWinners = async (e) => {
        e.preventDefault();
        setError('');
        setResult(null);
        setVideoInfo(null);
        setLoading(true);

        try {
            const response = await axios.post('/api/youtube/pick-comment-winner', {
                videoUrl,
                numberOfWinners: parseInt(numberOfWinners),
                filters: {
                    uniqueAuthors,
                    includeReplies,
                    textSearch: textSearch.trim(),
                    beforeDate: beforeDate ? new Date(beforeDate).toISOString() : null,
                    afterDate: afterDate ? new Date(afterDate).toISOString() : null
                }
            });

            if (response.data.success) {
                setResult(response.data);
                setVideoInfo(response.data.videoInfo);
            }
        } catch (err) {
            console.error(err);
            if (err.response?.status === 402) {
                setError(`Insufficient credits. You need ${err.response.data.creditsNeeded} credits.`);
            } else if (err.response?.status === 401) {
                setError('Please login to use this tool.');
            } else {
                setError(err.response?.data?.message || 'Failed to pick winners');
            }
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Verification function
    const handleVerifyRoll = async (e) => {
        e.preventDefault();
        if (!verificationId.trim()) {
            setVerificationError('Please enter a verification ID');
            return;
        }

        setVerifyingRoll(true);
        setVerificationError('');
        setVerificationResult(null);

        try {
            const response = await axios.get(`/api/youtube/verify/${verificationId.trim()}`);
            if (response.data.success) {
                setVerificationResult(response.data.data);
            }
        } catch (err) {
            console.error(err);
            setVerificationError(err.response?.data?.message || 'Failed to verify giveaway roll');
        } finally {
            setVerifyingRoll(false);
        }
    };

    // Reroll function
    const handleReroll = async (e) => {
        e.preventDefault();
        if (!result?.verificationId) {
            toast.error('No giveaway to reroll');
            return;
        }

        if (!rerollReason.trim()) {
            toast.error('Please provide a reason for rerolling');
            return;
        }

        setRerolling(true);

        try {
            const response = await axios.post('/api/youtube/reroll', {
                verificationId: result.verificationId,
                rerollReason: rerollReason.trim()
            });

            if (response.data.success) {
                setResult({
                    ...response.data,
                    videoInfo: result.videoInfo
                });
                setShowRerollModal(false);
                setRerollReason('');
                toast.success('Giveaway rerolled successfully!');
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to reroll giveaway');
        } finally {
            setRerolling(false);
        }
    };

    // Load giveaway history
    const loadGiveawayHistory = async () => {
        if (!isAuthenticated) return;

        setLoadingHistory(true);
        try {
            const response = await axios.get('/api/youtube/giveaway-history');
            if (response.data.success) {
                setGiveawayHistory(response.data.data);
                setShowHistory(true);
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to load giveaway history');
        } finally {
            setLoadingHistory(false);
        }
    };

    // Load recent giveaways
    const loadRecentGiveaways = async () => {
        setLoadingRecent(true);
        try {
            const response = await axios.get('/api/youtube/recent-giveaways');
            if (response.data.success) {
                setRecentGiveaways(response.data.data);
                setShowRecentGiveaways(true);
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to load recent giveaways');
        } finally {
            setLoadingRecent(false);
        }
    };

    return (
        <div className="twitter-tools-container">
            <div className="tools-header">
                <div className="badge-featured">YouTube Suite</div>
                <h1>🎯 YouTube Comment Picker</h1>
                <p>Fair and transparent winner selection from YouTube video comments</p>
            </div>

            {/* Verification Section */}
            <div className="tools-form-container">
                <div className="verification-section" style={{
                    background: 'rgba(255, 0, 0, 0.05)',
                    border: '2px solid rgba(255, 0, 0, 0.2)',
                    borderRadius: '16px',
                    padding: '2rem',
                    marginBottom: '2rem'
                }}>
                    <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>🔍 Verify Giveaway Roll</h3>
                    <form onSubmit={handleVerifyRoll} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <label htmlFor="verificationId" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Verification ID
                            </label>
                            <input
                                type="text"
                                id="verificationId"
                                value={verificationId}
                                onChange={(e) => setVerificationId(e.target.value.toUpperCase())}
                                placeholder="ABC123DEF456"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: '8px',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={verifyingRoll}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            {verifyingRoll ? '🔍 Verifying...' : '🔍 Verify'}
                        </button>
                    </form>
                    {verificationError && (
                        <div className="error-message" style={{ marginTop: '1rem' }}>
                            <span className="error-icon">⚠️</span>
                            {verificationError}
                        </div>
                    )}
                </div>

                {/* Main Picker Form */}
                <form onSubmit={handlePickWinners} className="tools-form">
                    <div className="form-group">
                        <label htmlFor="videoUrl">
                            YouTube Video URL *
                            <span className="label-hint">Full YouTube URL or video ID</span>
                        </label>
                        <input
                            type="text"
                            id="videoUrl"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                            required
                        />
                    </div>

                    {/* Filter Options */}
                    <div className="youtube-filters-section">
                        <div className="youtube-filters-header">
                            <div className="youtube-filters-header-icon">🎯</div>
                            <h3>Filter Options</h3>
                        </div>

                        <div className="youtube-filter-grid">
                            {/* Unique Authors */}
                            <div className="youtube-filter-card">
                                <label className="youtube-filter-label">
                                    <input
                                        type="checkbox"
                                        className="youtube-filter-input"
                                        checked={uniqueAuthors}
                                        onChange={(e) => setUniqueAuthors(e.target.checked)}
                                    />
                                    <div>
                                        <h4 className="youtube-filter-title">
                                            👤 Unique Authors
                                        </h4>
                                        <p className="youtube-filter-description">
                                            Select from unique users only (no duplicate entries)
                                        </p>
                                    </div>
                                </label>
                            </div>

                            {/* Include Replies */}
                            <div className="youtube-filter-card">
                                <label className="youtube-filter-label">
                                    <input
                                        type="checkbox"
                                        className="youtube-filter-input"
                                        checked={includeReplies}
                                        onChange={(e) => setIncludeReplies(e.target.checked)}
                                    />
                                    <div>
                                        <h4 className="youtube-filter-title">
                                            💬 Include Replies
                                        </h4>
                                        <p className="youtube-filter-description">
                                            Include comment replies in the selection pool
                                        </p>
                                    </div>
                                </label>
                            </div>

                            {/* Text Search */}
                            <div className="youtube-filter-card">
                                <label className="youtube-filter-title" style={{ marginBottom: '0.5rem', cursor: 'default' }}>
                                    🔍 Text Search
                                </label>
                                <input
                                    type="text"
                                    className="youtube-filter-text-input"
                                    value={textSearch}
                                    onChange={(e) => setTextSearch(e.target.value)}
                                    placeholder="Enter word, phrase, hashtag, or emoji..."
                                />
                                <p className="youtube-filter-description" style={{ marginTop: '0.5rem' }}>
                                    Only select from comments containing this text
                                </p>
                            </div>

                            {/* Date Filters */}
                            <div className="youtube-filter-card">
                                <label className="youtube-filter-title" style={{ marginBottom: '0.5rem', cursor: 'default' }}>
                                    📅 After Date
                                </label>
                                <input
                                    type="datetime-local"
                                    className="youtube-filter-datetime-input"
                                    value={afterDate}
                                    onChange={(e) => setAfterDate(e.target.value)}
                                />
                                <p className="youtube-filter-description" style={{ marginTop: '0.5rem' }}>
                                    Only comments posted after this date/time
                                </p>
                            </div>

                            <div className="youtube-filter-card">
                                <label className="youtube-filter-title" style={{ marginBottom: '0.5rem', cursor: 'default' }}>
                                    📅 Before Date
                                </label>
                                <input
                                    type="datetime-local"
                                    className="youtube-filter-datetime-input"
                                    value={beforeDate}
                                    onChange={(e) => setBeforeDate(e.target.value)}
                                />
                                <p className="youtube-filter-description" style={{ marginTop: '0.5rem' }}>
                                    Only comments posted before this date/time
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="numberOfWinners">
                            Number of Winners *
                            <span className="label-hint">1-50 winners</span>
                        </label>
                        <input
                            type="number"
                            id="numberOfWinners"
                            value={numberOfWinners}
                            onChange={(e) => setNumberOfWinners(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                            min="1"
                            max="50"
                            required
                        />
                    </div>

                    <button type="submit" className="tools-btn" disabled={loading || !isAuthenticated}>
                        {loading ? 'Picking Winners...' : `🎯 Pick ${numberOfWinners} Winner${numberOfWinners > 1 ? 's' : ''}`}
                    </button>
                </form>

                <div className="info-notice" style={{
                    marginTop: '1.5rem',
                    padding: '1rem 1.5rem',
                    background: 'rgba(255, 0, 0, 0.1)',
                    border: '2px solid rgba(255, 0, 0, 0.3)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                }}>
                    <span style={{ fontSize: '1.5rem' }}>💰</span>
                    <small style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                        <strong>Cost:</strong> 50 credits per winner selected.
                        <br />
                        <strong>Requirements:</strong> Video must have comments enabled and be publicly accessible.
                    </small>
                </div>

                {!isAuthenticated && (
                    <div className="info-notice" style={{
                        marginTop: '1rem',
                        padding: '1rem 1.5rem',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '2px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '12px'
                    }}>
                        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                            🔒 Please <a href="/login" style={{ color: 'var(--accent-color)' }}>log in</a> to use this tool.
                        </p>
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        <span className="error-icon">⚠️</span>
                        {error}
                    </div>
                )}
            </div>

            {videoInfo && (
                <div className="results-container">
                    <div className="results-section">
                        <h2>📹 Video Information</h2>
                        <div className="account-card">
                            <div className="account-header-info">
                                <img
                                    src={videoInfo.thumbnailUrl}
                                    alt={videoInfo.title}
                                    className="profile-image"
                                    style={{
                                        width: '120px',
                                        height: '90px',
                                        objectFit: 'cover',
                                        borderRadius: '12px',
                                        border: '4px solid #ff0000'
                                    }}
                                />
                                <div className="account-details">
                                    <h3 style={{
                                        margin: '0 0 0.5rem 0',
                                        fontSize: '1.1rem',
                                        color: 'var(--text-primary)',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}>
                                        {videoInfo.title}
                                    </h3>
                                    <p style={{
                                        margin: '0',
                                        color: 'var(--text-secondary)',
                                        fontSize: '0.9rem'
                                    }}>
                                        {videoInfo.channelTitle} • {videoInfo.viewCount.toLocaleString()} views
                                    </p>
                                    <p style={{
                                        margin: '0.25rem 0 0 0',
                                        color: 'var(--text-secondary)',
                                        fontSize: '0.8rem'
                                    }}>
                                        💬 {result.totalComments} comments available
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {result && result.winners && (
                <div className="results-container">
                    <div className="results-section">
                        <h2>🎉 Winners Selected</h2>

                        <div className="winners-grid">
                            {result.winners.map((winner, index) => (
                                <div key={winner.id} className="winner-card">
                                    <div className="winner-header">
                                        <div className="winner-number">#{index + 1}</div>
                                        <div className="winner-avatar">
                                            <img
                                                src={winner.authorProfileImageUrl}
                                                alt={winner.authorDisplayName}
                                                onError={(e) => {
                                                    e.target.src = '/default-avatar.png'; // Fallback avatar
                                                }}
                                            />
                                        </div>
                                        <div className="winner-info">
                                            <h4>{winner.authorDisplayName}</h4>
                                            <p className="winner-comment">
                                                {winner.textDisplay.length > 100
                                                    ? `${winner.textDisplay.substring(0, 100)}...`
                                                    : winner.textDisplay
                                                }
                                            </p>
                                        </div>
                                    </div>

                                    <div className="winner-actions">
                                        <button
                                            className="copy-btn"
                                            onClick={() => copyToClipboard(`@${winner.authorDisplayName}`)}
                                            title="Copy username"
                                        >
                                            📋 Copy Username
                                        </button>
                                        <button
                                            className="copy-btn"
                                            onClick={() => copyToClipboard(winner.textOriginal)}
                                            title="Copy full comment"
                                        >
                                            💬 Copy Comment
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="results-footer">
                            <small>
                                ℹ️ Selection completed on {new Date(result.selectedAt).toLocaleString()}
                                <br />
                                Verification ID: {result.verificationId}
                                <br />
                                Credits deducted: {result.creditsDeducted}
                            </small>
                        </div>
                    </div>
                </div>
            )}

            {/* Verification Result */}
            {verificationResult && (
                <div className="results-container">
                    <div className="results-section">
                        <h2>✅ Verification Result</h2>
                        <div className="verification-details">
                            <div className="verification-header">
                                <div className="verification-badge">
                                    <span className="verification-id">#{verificationResult.verificationId}</span>
                                    {verificationResult.isReroll && <span className="reroll-badge">REROLL</span>}
                                </div>
                                <div className="verification-meta">
                                    <p>Rolled by: <strong>{verificationResult.rolledBy}</strong></p>
                                    <p>Video: <strong>{verificationResult.videoTitle}</strong></p>
                                    <p>Channel: <strong>{verificationResult.channelTitle}</strong></p>
                                    <p>Total Comments: <strong>{verificationResult.totalComments}</strong></p>
                                    <p>Rolled: <strong>{new Date(verificationResult.rolledAt).toLocaleString()}</strong></p>
                                    {verificationResult.rerollReason && (
                                        <p>Reroll Reason: <strong>{verificationResult.rerollReason}</strong></p>
                                    )}
                                </div>
                                {verificationResult.filters && (
                                    <div className="verification-filters">
                                        <h4>🎯 Applied Filters:</h4>
                                        <div className="filters-summary">
                                            {verificationResult.filters.uniqueAuthors && (
                                                <span className="filter-tag">👤 Unique Authors</span>
                                            )}
                                            {verificationResult.filters.includeReplies && (
                                                <span className="filter-tag">💬 Include Replies</span>
                                            )}
                                            {verificationResult.filters.textSearch && (
                                                <span className="filter-tag">🔍 "{verificationResult.filters.textSearch}"</span>
                                            )}
                                            {verificationResult.filters.afterDate && (
                                                <span className="filter-tag">📅 After: {new Date(verificationResult.filters.afterDate).toLocaleString()}</span>
                                            )}
                                            {verificationResult.filters.beforeDate && (
                                                <span className="filter-tag">📅 Before: {new Date(verificationResult.filters.beforeDate).toLocaleString()}</span>
                                            )}
                                            {!verificationResult.filters.uniqueAuthors &&
                                             !verificationResult.filters.includeReplies &&
                                             !verificationResult.filters.textSearch &&
                                             !verificationResult.filters.afterDate &&
                                             !verificationResult.filters.beforeDate && (
                                                <span className="filter-tag">None</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="verified-winners">
                                <h3>🏆 Winners ({verificationResult.numberOfWinners})</h3>
                                <div className="winners-grid">
                                    {verificationResult.winners.map((winner, index) => (
                                        <div key={winner.id} className="winner-card">
                                            <div className="winner-header">
                                                <div className="winner-number">#{index + 1}</div>
                                                <div className="winner-avatar">
                                                    <img
                                                        src={winner.authorProfileImageUrl}
                                                        alt={winner.authorDisplayName}
                                                        onError={(e) => {
                                                            e.target.src = '/default-avatar.png';
                                                        }}
                                                    />
                                                </div>
                                                <div className="winner-info">
                                                    <h4>{winner.authorDisplayName}</h4>
                                                    <p className="winner-comment">
                                                        {winner.textDisplay.length > 100
                                                            ? `${winner.textDisplay.substring(0, 100)}...`
                                                            : winner.textDisplay
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {verificationResult.rollHistory && verificationResult.rollHistory.length > 1 && (
                                <div className="roll-history">
                                    <h3>📜 Roll History</h3>
                                    <div className="history-list">
                                        {verificationResult.rollHistory.map((roll, index) => (
                                            <div key={roll.verificationId} className={`history-item ${roll.isCurrentRoll ? 'current' : ''}`}>
                                                <span className="history-number">Roll #{roll.rollNumber}</span>
                                                <span className="history-id">{roll.verificationId}</span>
                                                <span className="history-date">{new Date(roll.rolledAt).toLocaleString()}</span>
                                                <span className="history-by">{roll.rolledBy}</span>
                                                {roll.rerollReason && <span className="history-reason">({roll.rerollReason})</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Reroll Modal */}
            {showRerollModal && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="modal-content" style={{
                        background: 'var(--bg-primary)',
                        borderRadius: '16px',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        border: '2px solid var(--border-color)'
                    }}>
                        <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>🔄 Reroll Giveaway</h3>
                        <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                            Provide a reason for rerolling this giveaway. This will create a new roll with fresh winners.
                        </p>

                        <form onSubmit={handleReroll}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                                    Reroll Reason *
                                </label>
                                <textarea
                                    value={rerollReason}
                                    onChange={(e) => setRerollReason(e.target.value)}
                                    placeholder="e.g., Previous winner didn't respond, technical issues, etc."
                                    required
                                    style={{
                                        width: '100%',
                                        minHeight: '80px',
                                        padding: '0.75rem',
                                        border: '2px solid var(--border-color)',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowRerollModal(false)}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-secondary)',
                                        border: '2px solid var(--border-color)',
                                        borderRadius: '8px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={rerolling}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {rerolling ? '🔄 Rerolling...' : '🔄 Reroll Giveaway'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            {result && result.verificationId && (
                <div className="action-buttons" style={{
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '2rem',
                    flexWrap: 'wrap'
                }}>
                    <button
                        onClick={() => setShowRerollModal(true)}
                        style={{
                            padding: '0.75rem 1.5rem',
                            background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        🔄 Reroll Giveaway
                    </button>
                    <button
                        onClick={() => copyToClipboard(`${window.location.origin}/youtube-comment-picker?v=${result.verificationId}`)}
                        style={{
                            padding: '0.75rem 1.5rem',
                            background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        🔗 Share Verification Link
                    </button>
                </div>
            )}

            {/* History Sections */}
            <div className="history-sections" style={{ marginBottom: '2rem' }}>
                {isAuthenticated && (
                    <button
                        onClick={loadGiveawayHistory}
                        disabled={loadingHistory}
                        style={{
                            padding: '0.75rem 1.5rem',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            marginRight: '1rem'
                        }}
                    >
                        {loadingHistory ? '📚 Loading...' : '📚 My Giveaways'}
                    </button>
                )}
                <button
                    onClick={loadRecentGiveaways}
                    disabled={loadingRecent}
                    style={{
                        padding: '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    {loadingRecent ? '🌟 Loading...' : '🌟 Recent Giveaways'}
                </button>
            </div>

            {/* Giveaway History */}
            {showHistory && giveawayHistory.length > 0 && (
                <div className="results-container">
                    <div className="results-section">
                        <h2>📚 My YouTube Giveaways</h2>
                        <div className="history-grid">
                            {giveawayHistory.map((giveaway) => (
                                <div key={giveaway._id} className="history-card">
                                    <div className="history-header">
                                        <span className="history-id">#{giveaway.verificationId}</span>
                                        <span className="history-date">{new Date(giveaway.rolledAt).toLocaleDateString()}</span>
                                    </div>
                                    <div className="history-details">
                                        <p><strong>Video:</strong> {giveaway.videoTitle}</p>
                                        <p><strong>Channel:</strong> {giveaway.channelTitle}</p>
                                        <p><strong>Winners:</strong> {giveaway.numberOfWinners}</p>
                                        <p><strong>Comments:</strong> {giveaway.totalComments}</p>
                                        <p><strong>Credits:</strong> {giveaway.creditsUsed}</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setVerificationId(giveaway.verificationId);
                                            handleVerifyRoll({ preventDefault: () => {} });
                                        }}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: 'var(--accent-color)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        View Details
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Recent Giveaways */}
            {showRecentGiveaways && recentGiveaways.length > 0 && (
                <div className="results-container">
                    <div className="results-section">
                        <h2>🌟 Recent YouTube Giveaways</h2>
                        <div className="recent-grid">
                            {recentGiveaways.map((giveaway) => (
                                <div key={giveaway._id} className="recent-card">
                                    <div className="recent-header">
                                        <span className="recent-id">#{giveaway.verificationId}</span>
                                        <span className="recent-by">by {giveaway.userId.username}</span>
                                    </div>
                                    <div className="recent-details">
                                        <p><strong>Video:</strong> {giveaway.videoTitle}</p>
                                        <p><strong>Channel:</strong> {giveaway.channelTitle}</p>
                                        <p><strong>Winners:</strong> {giveaway.numberOfWinners}</p>
                                        <p><strong>Rolled:</strong> {new Date(giveaway.rolledAt).toLocaleDateString()}</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setVerificationId(giveaway.verificationId);
                                            handleVerifyRoll({ preventDefault: () => {} });
                                        }}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: 'var(--accent-color)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        Verify
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="info-section">
                <h2>ℹ️ How It Works</h2>
                <div className="info-grid">
                    <div className="info-card">
                        <h3>🎯 Random Selection</h3>
                        <p>
                            Winners are selected using cryptographically secure random number generation.
                            Every comment has an exactly equal chance of being selected.
                        </p>
                    </div>
                    <div className="info-card">
                        <h3>💬 Comment Access</h3>
                        <p>
                            The tool fetches all top-level comments and replies from your YouTube video.
                            Comments must be publicly visible and the video must allow comments.
                        </p>
                    </div>
                    <div className="info-card">
                        <h3>🔒 Privacy & Security</h3>
                        <p>
                            Comment data is processed securely and not permanently stored.
                            We only access the information needed for fair winner selection.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default YouTubeCommentPicker;